/**
 * Validation regexes and functions aligned with backend requirements
 */

export const REGEX_NAME = /^[A-Za-zÀ-ÖØ-öø-ÿ'\s-]{1,50}$/;
export const REGEX_EMAIL = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export const REGEX_PHONE = /^\d{1,10}$/;
export { VAT_INPUT_PATTERN as REGEX_VAT, isValidVatNumber, normalizeVatNumber } from "./vatNumberUtils";
export const REGEX_SDI = /^[A-Z\d]{6,7}$/;
export const REGEX_PASSWORD = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@#$%^&+=!*]).{8,20}$/;
export const REGEX_CF = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$|^\d{11}$/;

/**
 * Normalizes a URL by prepending https:// if missing
 */
export const normalizeUrl = (url) => {
  if (!url) return null;
  return (!url.startsWith("https://") && !url.startsWith("http://")) 
    ? "https://" + url 
    : url;
};

/**
 * Validates domain parts and structure
 */
const validateWebsiteParts = (host) => {
  const parts = host.split('.');
  
  if (parts.includes("")) {
    const isTrailing = parts.at(-1) === "" && parts.length > 1;
    return { 
      isValid: false, 
      message: isTrailing ? "Completare l'estensione del dominio (es. .it)" : "Punti consecutivi o non validi nell'host" 
    };
  }

  const isWww = parts[0].toLowerCase() === "www";
  if (isWww && parts.length < 3) return { isValid: false, message: "Formato www.dominio.ext richiesto" };
  if (!isWww && parts.length < 2) return { isValid: false, message: "Formato dominio.ext richiesto" };

  const extension = parts.at(-1);
  if (!/^[a-zA-Z]{2,6}$/.test(extension)) return { isValid: false, message: "Estensione dominio non valida (2-6 lettere)" };

  return { isValid: true };
};

/**
 * Main validator for website URLs
 */
export const validateWebsite = (url) => {
  const testUrl = normalizeUrl(url);
  if (!testUrl) return { isValid: true };

  if (testUrl.length < 7 || testUrl.length > 40) return { isValid: false, message: "Lunghezza URL non valida (7-40 car.)" };
  if (!testUrl.startsWith("https://")) return { isValid: false, message: "Solo protocollo HTTPS supportato" };

  try {
    const urlObj = new URL(testUrl);
    const host = urlObj.hostname;
    if (!host) return { isValid: false, message: "Formato host non valido" };

    return validateWebsiteParts(host);
  } catch {
    return { isValid: false, message: "URL malformato" };
  }
};

export const validateSftpCatalogUrl = (url) => {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return { isValid: true };

  if (trimmed.length > 2048) {
    return { isValid: false, message: "URL troppo lungo" };
  }

  if (trimmed.toLowerCase().startsWith("sftp://")) {
    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== "sftp:" || !parsed.hostname) {
        return { isValid: false, message: "Formato URL SFTP non valido" };
      }
      return { isValid: true, normalized: trimmed };
    } catch {
      return { isValid: false, message: "Formato URL SFTP non valido" };
    }
  }

  const httpsValidation = validateWebsite(trimmed);
  if (!httpsValidation.isValid) {
    return httpsValidation;
  }
  return { isValid: true, normalized: normalizeUrl(trimmed) };
};
 